import { useCallback, useEffect, useRef, useState } from 'react';

import * as api from '../lib/accountClient.js';
import { clearState } from '../lib/storage.js';

/**
 * The signed-in account, and the progress sync that follows from having one.
 *
 * The important rule here, and the reason this hook exists at all: **premium
 * comes from the server or not at all.** It used to be a boolean in
 * localStorage, which anyone could set from the console. Now the only thing
 * the browser holds is a session cookie it cannot read, and entitlement is
 * whatever `/api/auth/me` says it is.
 *
 * Progress still lives in localStorage first — the app has to work offline and
 * before anyone signs up — and is mirrored to the server when there is an
 * account to mirror it to.
 */
export function useAccount() {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | offline
  const [error, setError] = useState(null);

  // Serialises pushes so a burst of changes can't land out of order.
  const pushing = useRef(Promise.resolve());
  const lastPushed = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const result = await api.me();
      setUser(result.user);
      setStatus('ready');
      return result.user;
    } catch (err) {
      // No server (a static build, or the API is down) is a normal state, not
      // an error to show: the app works without an account.
      setStatus(err.code === 'offline' ? 'offline' : 'ready');
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const signIn = useCallback(
    async (email, password) => {
      setError(null);
      try {
        const result = await api.login(email, password);
        setUser(result.user);
        return result.user;
      } catch (err) {
        setError(err.message);
        throw err;
      }
    },
    [],
  );

  const signUp = useCallback(async (email, password) => {
    setError(null);
    try {
      const result = await api.register(email, password);
      setUser(result.user);
      return result.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Finish a password reset. Signs in on success — the server has just issued
   * a session, and bouncing someone to a login form after they proved they own
   * the address would be a strange reward for it.
   */
  const applyReset = useCallback(async (token, password) => {
    setError(null);
    try {
      const result = await api.resetPassword(token, password);
      setUser(result.user);
      return result.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Erase the account, then land in the signed-out state.
   *
   * The local progress cache is cleared too. Leaving it behind would mean a
   * deleted account's streaks and saved verses reappearing for whoever opens
   * the browser next, which is not what "delete everything" means to the person
   * who asked for it.
   */
  const deleteAccount = useCallback(async (confirmation) => {
    await api.deleteAccount(confirmation);
    clearState();
    setUser(null);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      /* the cookie is gone either way as far as this tab is concerned */
    }
    setUser(null);
  }, []);

  /**
   * Mirror local progress up. Debounced by the caller; skipped when the
   * payload hasn't actually changed, so idle tabs don't write every few
   * seconds for nothing.
   */
  const syncUp = useCallback(
    (progress) => {
      if (!user) return;
      const serialised = JSON.stringify(progress);
      if (serialised === lastPushed.current) return;
      lastPushed.current = serialised;

      pushing.current = pushing.current
        .then(() => api.pushProgress(progress))
        .catch(() => {
          // A failed push is not worth interrupting anyone over — the local
          // copy is intact and the next change tries again.
          lastPushed.current = null;
        });
    },
    [user],
  );

  /** Pull the server's copy, for merging on sign-in. */
  const pullDown = useCallback(async () => {
    if (!user) return null;
    try {
      const result = await api.fetchProgress();
      return result.progress ?? null;
    } catch {
      return null;
    }
  }, [user]);

  /**
   * Wait for an entitlement the server doesn't know about yet.
   *
   * Coming back from a successful checkout, the browser arrives before Stripe's
   * webhook does — the redirect is instant and the event takes a moment. So the
   * return page asks again a few times rather than either believing the URL
   * (which would be a client-side grant, the exact thing this design refuses)
   * or showing "still not premium" to somebody who has just paid.
   *
   * Resolves true if it lands, false if it hasn't after ~20 seconds — at which
   * point the honest thing is to say it's processing, not to pretend.
   */
  const awaitPremium = useCallback(async ({ attempts = 10, delayMs = 2000 } = {}) => {
    for (let i = 0; i < attempts; i++) {
      const fresh = await refresh();
      if (fresh?.premium) return true;
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
    return false;
  }, [refresh]);

  return {
    user,
    // Server-authoritative. Never read a premium flag from local state.
    isPremium: !!user?.premium,
    signedIn: !!user,
    // Confirmed their address. Server-authoritative like `isPremium`: the point
    // of confirmation is that the browser cannot assert it.
    emailVerified: !!user?.emailVerified,
    status,
    error,
    clearError: () => setError(null),
    signIn,
    signUp,
    signOut,
    deleteAccount,
    applyReset,
    refresh,
    awaitPremium,
    syncUp,
    pullDown,
  };
}
